import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp,
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, Send, AlertCircle, CheckCircle, Play,
  XCircle, ArrowRight, Building, Phone, FileText, AlertTriangle, ArrowLeft,
  CreditCard, Download, Mail, Navigation, User, Wallet, Loader2, Percent
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import RouteVisualization from '@/components/transporter/RouteVisualization';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useTransporterRequests } from '@/hooks/useTransporterRequests';
import { useTransporterOffers } from '@/hooks/useTransporterOffers';
import { useTransporterBookings } from '@/hooks/useTransporterBookings';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { useTransporterVehicles } from '@/hooks/useTransporterVehicles';
import useConstants, { constantsUtils } from '@/hooks/useConstants';
import { api } from '@/services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const TransporterMissionsRequests = () => {
  const { stats } = useTransporterStats();
  const { vehicles } = useTransporterVehicles({});
  const { constants } = useConstants();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === 'requests' ? 'requests' : tab === 'offers' ? 'offers' : 'missions';
  });
  const [missionSubTab, setMissionSubTab] = useState(() => {
    const subTab = searchParams.get('missionSubTab');
    return subTab === 'history' ? 'history' : 'active';
  });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);

  // Mettre à jour l'URL quand l'onglet change
  useEffect(() => {
    const params = { tab: activeTab };
    if (activeTab === 'missions') {
      params.missionSubTab = missionSubTab;
    }
    setSearchParams(params, { replace: true });
  }, [activeTab, missionSubTab, setSearchParams]);

  // États pour les missions (bookings)
  const [missionFilters, setMissionFilters] = useState({
    status_code: 'all',
    payment_status: 'all',
    search: '',
  });

  // États pour les demandes
  const [requestFilters, setRequestFilters] = useState({
    search: '',
    urgency: 'all',
  });

  // États pour les offres
  const [offerFilters, setOfferFilters] = useState({
    status: 'all',
    search: '',
  });

  // État pour le dialogue d'offre
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerData, setOfferData] = useState({
    price: '',
    message: '',
    vehicle: '',
  });

  // État pour la modale de détails de demande
  const [requestDetailsDialogOpen, setRequestDetailsDialogOpen] = useState(false);
  const [requestDetails, setRequestDetails] = useState(null);
  const [requestDetailsLoading, setRequestDetailsLoading] = useState(false);

  // Utiliser les bookings (offres acceptées) pour les missions
  const { bookings: missions, loading: missionsLoading, error: missionsError, fetchBookings } = useTransporterBookings(missionFilters);
  const { requests, loading: requestsLoading, error: requestsError, makeOffer } = useTransporterRequests(requestFilters);
  const { offers, loading: offersLoading, error: offersError, fetchOffers } = useTransporterOffers(offerFilters);

  // Fonctions pour démarrer/terminer une mission (utiliser les endpoints existants)
  const startMission = async (missionId) => {
    try {
      const { transporterAPI } = await import('@/services/api');
      await transporterAPI.startMission(missionId);
      fetchBookings();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.detail || 'Erreur lors du démarrage de la mission'
      };
    }
  };

  const deliverMission = async (missionId) => {
    try {
      const { transporterAPI } = await import('@/services/api');
      await transporterAPI.deliverMission(missionId);
      fetchBookings();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.detail || 'Erreur lors de la livraison de la mission'
      };
    }
  };

  const completeMission = async (missionId) => {
    try {
      const { transporterAPI } = await import('@/services/api');
      await transporterAPI.completeMission(missionId);
      fetchBookings();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.detail || 'Erreur lors de la finalisation de la mission'
      };
    }
  };

  const sidebarLinks = getTransporterSidebarLinks(stats);

  // Fonctions utilitaires
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Formater avec séparateur de milliers (espace)
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `${formatted} GNF`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString('fr-FR', options);
  };

  const getPaymentMethodIcon = () => {
    return <CreditCard className="w-4 h-4" />;
  };

  const getPaymentStatusBadgeForPayment = (status) => {
    if (!constants) return <Badge className="bg-gray-500 text-white">{status}</Badge>;
    const label = constantsUtils.getLabel(constants.payment_status, status);
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-500' },
      'SUCCESS': { color: 'bg-green-500' },
      'FAILED': { color: 'bg-red-500' },
      'REFUNDED': { color: 'bg-orange-500' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500' };
    return (
      <Badge className={`${config.color} text-white`}>
        {label}
      </Badge>
    );
  };

  const handleViewProof = (proofUrl) => {
    setSelectedProof(proofUrl);
    setProofDialogOpen(true);
  };

  const handleDownloadProof = (proofUrl, paymentId) => {
    const link = document.createElement('a');
    link.href = proofUrl;
    link.download = `preuve_paiement_${paymentId.slice(-8)}.${proofUrl.split('.').pop()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewPayments = async (missionId) => {
    try {
      const { transporterAPI } = await import('@/services/api');
      const response = await transporterAPI.getBookingById(missionId);
      setSelectedMission(response.data);
      setPaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paiements',
        variant: 'destructive',
      });
    }
  };

  // Fonction utilitaire pour obtenir la valeur d'un statut depuis son label
  // Exemple: getStatusValue('booking', 'Confirmée') => 'CONFIRMED'
  const getStatusValue = (statusType, label) => {
    if (!constants) return null;
    const choices = statusType === 'booking'
      ? constants.booking_status
      : statusType === 'transport_request'
      ? constants.transport_request_status
      : null;
    return choices ? constantsUtils.getValue(choices, label) : null;
  };

  // Configuration des couleurs et icônes pour les statuts (présentation uniquement)
  // Les labels viennent du backend via les constantes
  const getStatusColorAndIcon = (status, statusType) => {
    if (statusType === 'booking') {
      const config = {
        'CONFIRMED': { color: 'bg-blue-500', icon: CheckCircle },
        'IN_PROGRESS': { color: 'bg-green-500', icon: Play },
        'DELIVERED': { color: 'bg-purple-500', icon: Package },
        'COMPLETED': { color: 'bg-gray-500', icon: CheckCircle },
        'CANCELLED': { color: 'bg-red-500', icon: XCircle },
        'DISPUTED': { color: 'bg-orange-500', icon: AlertTriangle },
      };
      return config[status] || { color: 'bg-gray-500', icon: Clock };
    } else if (statusType === 'transport_request') {
      const config = {
        'DRAFT': { color: 'bg-gray-500' },
        'PUBLISHED': { color: 'bg-blue-500' },
        'ASSIGNED': { color: 'bg-purple-500' },
        'IN_TRANSIT': { color: 'bg-green-500' },
        'COMPLETED': { color: 'bg-green-600' },
        'CANCELLED': { color: 'bg-red-500' },
      };
      return config[status] || { color: 'bg-gray-500' };
    }
    return { color: 'bg-gray-500', icon: Clock };
  };

  // Badges pour missions (bookings) - utilise les constantes du backend
  const getMissionStatusBadge = (status) => {
    if (!constants) {
      // Fallback si les constantes ne sont pas encore chargées
      return <Badge className="bg-gray-500 text-white">{status}</Badge>;
    }

    const label = constantsUtils.getLabel(constants.booking_status, status);
    const { color, icon: Icon } = getStatusColorAndIcon(status, 'booking');

    return (
      <Badge className={`${color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  // Badge pour le statut de livraison (utilise le statut de la demande) - utilise les constantes du backend
  const getDeliveryStatusBadge = (requestStatus) => {
    if (!constants) {
      // Fallback si les constantes ne sont pas encore chargées
      return <Badge className="bg-gray-500 text-white">{requestStatus}</Badge>;
    }

    const label = constantsUtils.getLabel(constants.transport_request_status, requestStatus);
    const { color } = getStatusColorAndIcon(requestStatus, 'transport_request');

    return (
      <Badge className={`${color} text-white`}>
        {label}
      </Badge>
    );
  };

  // Badge pour le statut de paiement
  const getPaymentStatusBadge = (booking) => {
    const paymentPercentage = booking.payment_percentage || 0;
    const totalPaid = booking.total_paid || 0;

    if (paymentPercentage >= 100) {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Payé totalement (100%)
        </Badge>
      );
    } else if (paymentPercentage > 0) {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Payé partiellement ({paymentPercentage}%)
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500 text-white flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Non payé (0%)
        </Badge>
      );
    }
  };

  // Badges pour demandes
  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      'NORMAL': { label: 'Normal', color: 'bg-blue-500' },
      'URGENT': { label: 'Urgent', color: 'bg-red-500' },
    };

    const config = urgencyConfig[urgency] || { label: urgency, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getPaymentTermsLabel = (terms) => {
    if (!terms || !constants?.payment_terms) return terms || 'N/A';

    // Chercher le label correspondant au code dans les constantes
    const paymentTerm = constants.payment_terms.find(term => term[0] === terms);
    return paymentTerm ? paymentTerm[1] : terms;
  };

  const getPaymentTermsBadge = (terms) => {
    const termsConfig = {
      'PREPAID': { label: 'Prépaiement', color: 'bg-green-500' },
      'COD': { label: 'Paiement à la livraison', color: 'bg-yellow-500' },
      'POSTPAID': { label: 'Paiement différé', color: 'bg-blue-500' },
    };

    const config = termsConfig[terms] || { label: terms, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  // Gestionnaires d'événements
  const handleStartMission = async (missionId) => {
    const result = await startMission(missionId);
    if (result.success) {
      toast({
        title: 'Mission démarrée',
        description: 'La mission a été démarrée avec succès.',
      });
      fetchBookings();
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleDeliverMission = async (missionId) => {
    const result = await deliverMission(missionId);
    if (result.success) {
      toast({
        title: 'Mission livrée',
        description: 'La mission a été marquée comme livrée avec succès.',
      });
      fetchBookings();
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleCompleteMission = async (missionId) => {
    const result = await completeMission(missionId);
    if (result.success) {
      toast({
        title: 'Mission terminée',
        description: 'La mission a été terminée avec succès.',
      });
      fetchBookings();
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleUpdateBookingStatus = async (missionId, newStatus) => {
    try {
      const response = await transporterAPI.updateBookingStatus(missionId, newStatus);
      toast({
        title: 'Statut mis à jour',
        description: response.data.detail || 'Le statut de la mission a été mis à jour avec succès.',
      });
      fetchBookings();
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour du statut.';
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
      return { success: false, error: errorMessage };
    }
  };

  const handleMakeOffer = (request) => {
    setSelectedRequest(request);
    setOfferData({
      price: '',
      message: '',
      vehicle: '',
    });
    setOfferDialogOpen(true);
  };

  // Fonction pour charger et afficher les détails d'une demande
  const handleViewRequestDetails = async (requestId) => {
    try {
      setRequestDetailsLoading(true);
      setRequestDetails(null);
      const response = await api.get(`/transport/requests/${requestId}/`);
      setRequestDetails(response.data);
      setRequestDetailsDialogOpen(true);
    } catch (err) {
      console.error('Erreur lors du chargement des détails de la demande:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails de la demande.',
        variant: 'destructive',
      });
    } finally {
      setRequestDetailsLoading(false);
    }
  };

  const handleSubmitOffer = async () => {
    if (!offerData.price || !offerData.vehicle) {
      toast({
        title: 'Erreur',
        description: 'Le prix et le véhicule sont requis.',
        variant: 'destructive',
      });
      return;
    }

    const result = await makeOffer(selectedRequest.id, offerData);
    if (result.success) {
      toast({
        title: 'Offre envoyée',
        description: 'Votre offre a été envoyée avec succès.',
      });
      setOfferDialogOpen(false);
      // Recharger les offres pour afficher la nouvelle offre
      fetchOffers();
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Missions & Demandes - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Missions & Demandes</h1>
            <p className="text-muted-foreground">Gérez vos missions actives et découvrez de nouvelles opportunités</p>
          </div>
          {stats.nouvelles_demandes > 0 && (
            <Badge className="bg-red-500 text-white text-lg px-4 py-2 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {stats.nouvelles_demandes} nouvelles demandes
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="missions" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Mes Missions
              {missions.length > 0 && (
                <Badge variant="secondary" className="ml-2">{missions.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Demandes Disponibles
              {stats.nouvelles_demandes > 0 && (
                <Badge className="ml-2 bg-red-500">{stats.nouvelles_demandes}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="offers" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Mes Offres
              {offers.filter(o => o.status === 'PENDING').length > 0 && (
                <Badge className="ml-2 bg-yellow-500">{offers.filter(o => o.status === 'PENDING').length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Onglet Missions */}
          <TabsContent value="missions" className="space-y-6">
            {/* Sous-onglets pour Missions actives / Historique */}
            <Tabs value={missionSubTab} onValueChange={setMissionSubTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="active" className="flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Mes Missions
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historique
                </TabsTrigger>
              </TabsList>

              {/* Sous-onglet: Mes Missions (actives) */}
              <TabsContent value="active" className="space-y-6">
                {/* Filtres */}
                <Card className="bg-card border border-border">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Rechercher une mission..."
                            value={missionFilters.search}
                            onChange={(e) => setMissionFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <Select
                        value={missionFilters.status_code}
                        onValueChange={(value) => setMissionFilters(prev => ({ ...prev, status_code: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          {constants?.booking_status?.filter(([value]) =>
                            ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'].includes(value)
                          ).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={missionFilters.payment_status}
                        onValueChange={(value) => setMissionFilters(prev => ({ ...prev, payment_status: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par paiement" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les paiements</SelectItem>
                          <SelectItem value="paid">Payé totalement</SelectItem>
                          <SelectItem value="partial">Payé partiellement</SelectItem>
                          <SelectItem value="unpaid">Non payé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Liste des missions actives */}
            {missionsLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-muted-foreground">Chargement des missions...</div>
                </CardContent>
              </Card>
            ) : missionsError ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-destructive">Erreur: {missionsError}</div>
                </CardContent>
              </Card>
            ) : missions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Truck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune mission trouvée</h3>
                  <p className="text-muted-foreground">Vous n'avez pas encore de missions assignées.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {missions
                  .filter(mission => {
                    // Filtrer par statut actif (pour le sous-onglet actif)
                    const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                    if (missionSubTab === 'active' && !activeStatuses.includes(mission.status_code)) {
                      return false;
                    }
                    if (missionSubTab === 'history' && activeStatuses.includes(mission.status_code)) {
                      return false;
                    }

                    // Filtre par recherche
                    if (missionFilters.search) {
                      const searchLower = missionFilters.search.toLowerCase();
                      const request = mission.request_detail || mission.request;
                      const matchesSearch =
                        mission.id?.toLowerCase().includes(searchLower) ||
                        request?.from_location?.city?.toLowerCase().includes(searchLower) ||
                        request?.from_location?.label?.toLowerCase().includes(searchLower) ||
                        request?.to_location?.city?.toLowerCase().includes(searchLower) ||
                        request?.to_location?.label?.toLowerCase().includes(searchLower) ||
                        mission.vehicle_detail?.plate_number?.toLowerCase().includes(searchLower) ||
                        mission.vehicle?.plate_number?.toLowerCase().includes(searchLower) ||
                        formatCurrency(mission.transporter_amount || 0).toLowerCase().includes(searchLower);
                      if (!matchesSearch) return false;
                    }

                    // Filtre par statut
                    if (missionFilters.status_code !== 'all' && mission.status_code !== missionFilters.status_code) {
                      return false;
                    }

                    // Filtre par statut de paiement
                    if (missionFilters.payment_status !== 'all') {
                      const paymentPercentage = mission.payment_percentage || 0;
                      if (missionFilters.payment_status === 'paid' && paymentPercentage < 100) return false;
                      if (missionFilters.payment_status === 'partial' && (paymentPercentage === 0 || paymentPercentage >= 100)) return false;
                      if (missionFilters.payment_status === 'unpaid' && paymentPercentage > 0) return false;
                    }

                    return true;
                  })
                  .map((mission) => {
                    const request = mission.request_detail || mission.request;
                    const driver = mission.driver_detail || mission.driver;
                    const payments = mission.payments || [];

                    return (
                      <Card key={mission.id} className="bg-card border border-border">
                        <CardContent className="p-4 space-y-3">
                          {/* En-tête simple : RESERVATION et PAYMENT */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">RESERVATION</span>
                              <span className="text-sm font-bold">#{mission.id?.slice(-8) || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">PAYMENT</span>
                              <div className="flex flex-col items-end">
                                <span className="text-lg font-bold text-secondary">{formatCurrency(mission.transporter_amount || 0)}</span>
                                <span className="text-xs text-muted-foreground">Prix du transport</span>
                              </div>
                            </div>
                          </div>

                          {/* Badges de statut */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {getMissionStatusBadge(mission.status_code)}
                            {getPaymentStatusBadge(mission)}
                          </div>

                          {/* Barre de progression */}
                          {mission.payment_percentage !== undefined && (
                            <div>
                              <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${
                                    mission.payment_percentage >= 100 ? 'bg-green-500' :
                                    mission.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(mission.payment_percentage, 100)}%` }}
                                />
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {mission.payment_percentage}% ({formatCurrency(mission.total_paid || 0)} / {formatCurrency(mission.transporter_amount || 0)})
                              </div>
                            </div>
                          )}

                          {/* Visualisation de route simple */}
                          {request && (
                            <div className="flex items-center gap-2 py-2">
                              <div className="flex flex-col items-center flex-1">
                                <MapPin className="w-5 h-5 text-green-500" />
                                <span className="text-xs font-medium mt-1">
                                  {request?.from_location_detail?.city || request?.from_location_detail?.label ||
                                   request?.from_location?.city || request?.from_location?.label || 'N/A'}
                                </span>
                              </div>
                              <div className="flex-1 flex items-center justify-center">
                                <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                <Truck className="w-5 h-5 text-green-500 mx-2" />
                                <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                              </div>
                              <div className="flex flex-col items-center flex-1">
                                <Navigation className="w-5 h-5 text-green-500" />
                                <span className="text-xs font-medium mt-1">
                                  {request?.to_location_detail?.city || request?.to_location_detail?.label ||
                                   request?.to_location?.city || request?.to_location?.label || 'N/A'}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Informations en deux colonnes */}
                          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                            {/* Colonne Transport */}
                            <div>
                              <div className="flex items-center gap-1.5 mb-2">
                                <Truck className="w-4 h-4 text-muted-foreground" />
                                <span className="text-xs font-semibold">Transport</span>
                              </div>
                              <div className="space-y-1 text-xs">
                                {request?.preferred_pickup_date && (
                                  <div>
                                    <span className="text-muted-foreground">Départ: </span>
                                    <span className="font-medium">{formatDateOnly(request.preferred_pickup_date)}</span>
                                  </div>
                                )}
                                {request?.preferred_delivery_date && (
                                  <div>
                                    <span className="text-muted-foreground">Arrivée: </span>
                                    <span className="font-medium">{formatDateOnly(request.preferred_delivery_date)}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-muted-foreground">Véhicule: </span>
                                  <span className="font-medium">{mission.vehicle_detail?.plate_number || mission.vehicle?.plate_number || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Colonne Transporteur */}
                            <div>
                              <div className="flex items-center gap-1.5 mb-2">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="text-xs font-semibold">Transporteur</span>
                              </div>
                              <div className="space-y-1 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Chauffeur: </span>
                                  <span className="font-medium">
                                    {driver?.full_name ||
                                     (driver?.first_name && driver?.last_name
                                       ? `${driver.first_name} ${driver.last_name}`.trim()
                                       : driver?.first_name || '-')}
                                  </span>
                                </div>
                                {driver?.phone && (
                                  <div>
                                    <span className="text-muted-foreground">Tél: </span>
                                    <span className="font-medium">{driver.phone}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Termes */}
                          {request?.payment_terms && (
                            <div className="pt-2 border-t border-border">
                              <div className="flex items-center gap-1.5 mb-1">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="text-xs font-semibold">Termes</span>
                              </div>
                              <div className="text-xs pl-5">
                                <span className="text-muted-foreground">Paiement: </span>
                                <span className="font-medium">{getPaymentTermsLabel(request.payment_terms)}</span>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-2 border-t border-border">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewPayments(mission.id)}
                              className="flex-1"
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              Paiements ({payments.length})
                            </Button>
                            {mission.status_code === 'CONFIRMED' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleStartMission(mission.id)}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Démarrer
                              </Button>
                            )}
                            {mission.status_code === 'IN_PROGRESS' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleDeliverMission(mission.id)}
                                className="flex-1 bg-purple-600 hover:bg-purple-700"
                              >
                                <Package className="w-4 h-4 mr-2" />
                                Livrer
                              </Button>
                            )}
                            {mission.status_code === 'DELIVERED' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleCompleteMission(mission.id)}
                                className="flex-1 bg-gray-600 hover:bg-gray-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Terminer
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
              </TabsContent>

              {/* Sous-onglet: Historique (terminées) */}
              <TabsContent value="history" className="space-y-6">
                {/* Filtres */}
                <Card className="bg-card border border-border">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Rechercher une mission..."
                            value={missionFilters.search}
                            onChange={(e) => setMissionFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <Select
                        value={missionFilters.status_code}
                        onValueChange={(value) => setMissionFilters(prev => ({ ...prev, status_code: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          {constants?.booking_status?.filter(([value]) =>
                            ['COMPLETED', 'CANCELLED', 'DISPUTED'].includes(value)
                          ).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={missionFilters.payment_status}
                        onValueChange={(value) => setMissionFilters(prev => ({ ...prev, payment_status: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par paiement" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les paiements</SelectItem>
                          <SelectItem value="paid">Payé totalement</SelectItem>
                          <SelectItem value="partial">Payé partiellement</SelectItem>
                          <SelectItem value="unpaid">Non payé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Liste des missions historiques */}
                {missionsLoading ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <div className="text-muted-foreground">Chargement de l'historique...</div>
                    </CardContent>
                  </Card>
                ) : missionsError ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <div className="text-destructive">Erreur: {missionsError}</div>
                    </CardContent>
                  </Card>
                ) : missions.filter(mission => {
                  const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                  return !activeStatuses.includes(mission.status_code);
                }).length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <History className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucune mission dans l'historique</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore de missions terminées ou annulées.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {missions
                      .filter(mission => {
                        // Filtrer uniquement les missions terminées (historique)
                        const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                        if (activeStatuses.includes(mission.status_code)) {
                          return false;
                        }

                        // Filtre par recherche
                        if (missionFilters.search) {
                          const searchLower = missionFilters.search.toLowerCase();
                          const request = mission.request_detail || mission.request;
                          const matchesSearch =
                            mission.id?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.label?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.label?.toLowerCase().includes(searchLower) ||
                            mission.vehicle_detail?.plate_number?.toLowerCase().includes(searchLower) ||
                            mission.vehicle?.plate_number?.toLowerCase().includes(searchLower) ||
                            formatCurrency(mission.transporter_amount || 0).toLowerCase().includes(searchLower);
                          if (!matchesSearch) return false;
                        }

                        // Filtre par statut
                        if (missionFilters.status_code !== 'all' && mission.status_code !== missionFilters.status_code) {
                          return false;
                        }

                        // Filtre par statut de paiement
                        if (missionFilters.payment_status !== 'all') {
                          const paymentPercentage = mission.payment_percentage || 0;
                          if (missionFilters.payment_status === 'paid' && paymentPercentage < 100) return false;
                          if (missionFilters.payment_status === 'partial' && (paymentPercentage === 0 || paymentPercentage >= 100)) return false;
                          if (missionFilters.payment_status === 'unpaid' && paymentPercentage > 0) return false;
                        }

                        return true;
                      })
                      .map((mission) => {
                        const request = mission.request_detail || mission.request;
                        const driver = mission.driver_detail || mission.driver;
                        const payments = mission.payments || [];

                        return (
                          <Card key={mission.id} className="bg-card border border-border">
                            <CardContent className="p-4 space-y-3">
                              {/* En-tête simple : RESERVATION et PAYMENT */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">RESERVATION</span>
                                  <span className="text-sm font-bold">#{mission.id?.slice(-8) || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">PAYMENT</span>
                                  <div className="flex flex-col items-end">
                                    <span className="text-lg font-bold text-secondary">{formatCurrency(mission.transporter_amount || 0)}</span>
                                    <span className="text-xs text-muted-foreground">Prix du transport</span>
                                  </div>
                                </div>
                              </div>

                              {/* Badges de statut */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {getMissionStatusBadge(mission.status_code)}
                                {getPaymentStatusBadge(mission)}
                              </div>

                              {/* Barre de progression */}
                              {mission.payment_percentage !== undefined && (
                                <div>
                                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        mission.payment_percentage >= 100 ? 'bg-green-500' :
                                        mission.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(mission.payment_percentage, 100)}%` }}
                                    />
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {mission.payment_percentage}% ({formatCurrency(mission.total_paid || 0)} / {formatCurrency(mission.transporter_amount || 0)})
                                  </div>
                                </div>
                              )}

                              {/* Visualisation de route simple */}
                              {request && (
                                <div className="flex items-center gap-2 py-2">
                                  <div className="flex flex-col items-center flex-1">
                                    <MapPin className="w-5 h-5 text-green-500" />
                                    <span className="text-xs font-medium mt-1">
                                      {request?.from_location_detail?.city || request?.from_location_detail?.label ||
                                       request?.from_location?.city || request?.from_location?.label || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex-1 flex items-center justify-center">
                                    <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                    <Truck className="w-5 h-5 text-green-500 mx-2" />
                                    <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                  </div>
                                  <div className="flex flex-col items-center flex-1">
                                    <Navigation className="w-5 h-5 text-green-500" />
                                    <span className="text-xs font-medium mt-1">
                                      {request?.to_location_detail?.city || request?.to_location_detail?.label ||
                                       request?.to_location?.city || request?.to_location?.label || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Informations en deux colonnes */}
                              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                                {/* Colonne Transport */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transport</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    {request?.preferred_pickup_date && (
                                      <div>
                                        <span className="text-muted-foreground">Départ: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_pickup_date)}</span>
                                      </div>
                                    )}
                                    {request?.preferred_delivery_date && (
                                      <div>
                                        <span className="text-muted-foreground">Arrivée: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_delivery_date)}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">Véhicule: </span>
                                      <span className="font-medium">{mission.vehicle_detail?.plate_number || mission.vehicle?.plate_number || '-'}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Colonne Transporteur */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transporteur</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Chauffeur: </span>
                                      <span className="font-medium">
                                        {driver?.full_name ||
                                         (driver?.first_name && driver?.last_name
                                           ? `${driver.first_name} ${driver.last_name}`.trim()
                                           : driver?.first_name || '-')}
                                      </span>
                                    </div>
                                    {driver?.phone && (
                                      <div>
                                        <span className="text-muted-foreground">Tél: </span>
                                        <span className="font-medium">{driver.phone}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Termes */}
                              {request?.payment_terms && (
                                <div className="pt-2 border-t border-border">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Termes</span>
                                  </div>
                                  <div className="text-xs pl-5">
                                    <span className="text-muted-foreground">Paiement: </span>
                                    <span className="font-medium">{getPaymentTermsLabel(request.payment_terms)}</span>
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex items-center gap-2 pt-2 border-t border-border">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewPayments(mission.id)}
                                  className="flex-1"
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Paiements ({payments.length})
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Onglet Demandes */}
          <TabsContent value="requests" className="space-y-6">
            {/* Filtres */}
            <Card className="bg-card border border-border">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Rechercher une demande..."
                        value={requestFilters.search}
                        onChange={(e) => setRequestFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select
                    value={requestFilters.urgency}
                    onValueChange={(value) => setRequestFilters(prev => ({ ...prev, urgency: value }))}
                  >
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Filtrer par urgence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les urgences</SelectItem>
                      <SelectItem value="NORMAL">Normal</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Liste des demandes */}
            {requestsLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-muted-foreground">Chargement des demandes...</div>
                </CardContent>
              </Card>
            ) : requestsError ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-destructive">Erreur: {requestsError}</div>
                </CardContent>
              </Card>
            ) : requests.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune demande trouvée</h3>
                  <p className="text-muted-foreground">Aucune nouvelle demande disponible pour le moment.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {requests.map((request) => {
                  const fromLocation = request.from_location_detail || request.from_location;
                  const toLocation = request.to_location_detail || request.to_location;
                  const fromCity = fromLocation?.city || fromLocation?.label || 'N/A';
                  const toCity = toLocation?.city || toLocation?.label || 'N/A';
                  // Utiliser declared_value (prix déclaré par le client)
                  const displayPrice = request.declared_value;

                  return (
                    <Card
                      key={request.id}
                      className="group border-border bg-card/50 hover:border-secondary hover:shadow-lg transition-all duration-300 overflow-hidden"
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                          {/* En-tête avec ID et badges */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="text-lg font-bold text-foreground">Demande #{request.id?.slice(-8) || 'N/A'}</h3>
                                {getUrgencyBadge(request.urgency_level)}
                                {getPaymentTermsBadge(request.payment_terms)}
                                {/* Badge de commission */}
                                {request.commission_type === 'PERCENTAGE' && request.commission_percentage && (
                                  <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                    <Percent className="w-3 h-3 mr-1" />
                                    Commission: {request.commission_percentage}%
                                  </Badge>
                                )}
                                {request.commission_type === 'FIXED_AMOUNT' && request.commission_fixed_amount && (
                                  <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                    <DollarSign className="w-3 h-3 mr-1" />
                                    Commission: {formatCurrency(request.commission_fixed_amount)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Trajet et Prix */}
                          <div className="flex items-center justify-between gap-4 p-4 bg-primary/5 rounded-lg border border-border/50">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                                  <MapPin className="w-6 h-6 text-primary" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-base">
                                  <span className="font-bold text-foreground truncate">{fromCity}</span>
                                  <span className="text-muted-foreground flex-shrink-0">→</span>
                                  <span className="font-bold text-foreground truncate">{toCity}</span>
                                </div>
                              </div>
                            </div>
                            {displayPrice && (
                              <div className="flex-shrink-0 text-right">
                                <div className="text-2xl font-bold text-secondary">
                                  {formatCurrency(displayPrice)}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Informations supplémentaires */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {request.preferred_pickup_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">Départ: {formatDateOnly(request.preferred_pickup_date)}</span>
                              </div>
                            )}
                            {request.preferred_delivery_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">Arrivée: {formatDateOnly(request.preferred_delivery_date)}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              <span>Publiée le {formatDateOnly(request.created_at)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="pt-2 border-t border-border flex gap-2">
                            <Button
                              onClick={() => handleViewRequestDetails(request.id)}
                              variant="outline"
                              className="flex-1"
                              size="sm"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Détails
                            </Button>
                            <Button
                              onClick={() => handleMakeOffer(request)}
                              className="flex-1 bg-primary hover:bg-primary/90"
                              size="sm"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Faire une offre
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Onglet Mes Offres */}
          <TabsContent value="offers" className="space-y-6">
            {/* Filtres */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Rechercher une offre..."
                        value={offerFilters.search}
                        onChange={(e) => setOfferFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={offerFilters.status} onValueChange={(value) => setOfferFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="PENDING">En attente</SelectItem>
                      <SelectItem value="ACCEPTED">Acceptée</SelectItem>
                      <SelectItem value="REJECTED">Rejetée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Liste des offres */}
            {offersLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Chargement des offres...</p>
                </CardContent>
              </Card>
            ) : offersError ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Erreur</h3>
                  <p className="text-muted-foreground">{offersError}</p>
                </CardContent>
              </Card>
            ) : offers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune offre</h3>
                  <p className="text-muted-foreground">Vous n'avez pas encore fait d'offres sur des demandes.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {offers
                  .filter(offer => {
                    if (offerFilters.status !== 'all' && offer.status !== offerFilters.status) return false;
                    if (offerFilters.search) {
                      const searchLower = offerFilters.search.toLowerCase();
                      const request = offer.request_detail || offer.request;
                      return (
                        request?.id?.toLowerCase().includes(searchLower) ||
                        (request?.from_location?.city || request?.from_location?.label || '')?.toLowerCase().includes(searchLower) ||
                        (request?.to_location?.city || request?.to_location?.label || '')?.toLowerCase().includes(searchLower) ||
                        offer.price?.toString().includes(searchLower)
                      );
                    }
                    return true;
                  })
                  .map((offer) => {
                    // Utiliser request_detail s'il est disponible (pour les détails complets), sinon request
                    const request = offer.request_detail || offer.request;

                    const getOfferStatusBadge = (status) => {
                      const statusConfig = {
                        'PENDING': { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
                        'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500', icon: CheckCircle },
                        'REJECTED': { label: 'Rejetée', color: 'bg-red-500', icon: XCircle },
                        'EXPIRED': { label: 'Expirée', color: 'bg-gray-500', icon: AlertCircle },
                      };
                      const config = statusConfig[status] || statusConfig.PENDING;
                      const Icon = config.icon;
                      return (
                        <Badge className={`${config.color} text-white flex items-center gap-1`}>
                          <Icon className="w-3 h-3" />
                          {config.label}
                        </Badge>
                      );
                    };

                    const getRequestStatusBadge = (status) => {
                      const statusConfig = {
                        'PUBLISHED': { label: 'Publiée', color: 'bg-blue-500' },
                        'ASSIGNED': { label: 'Assignée', color: 'bg-green-500' },
                        'IN_TRANSIT': { label: 'En transit', color: 'bg-purple-500' },
                        'COMPLETED': { label: 'Terminée', color: 'bg-green-600' },
                        'CANCELLED': { label: 'Annulée', color: 'bg-red-500' },
                      };
                      const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
                      return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
                    };

                    const vehicle = offer.vehicle_detail || offer.vehicle;
                    // Prix déclaré par le client
                    const clientPrice = request?.declared_value;
                    // Prix de l'offre du transporteur
                    const offerPrice = offer.price;

                    return (
                      <Card key={offer.id} className="border-border bg-primary-700/20 hover:border-secondary transition-colors">
                        <CardContent className="p-6">
                          <div className="flex flex-col gap-6">
                            <div className="space-y-6">
                              {/* En-tête avec numéro d'offre, statut et prix */}
                              <div className="space-y-3 pb-4 border-b border-border">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <h3 className="text-lg font-bold text-foreground">Offre #{offer.id?.slice(-8) || 'N/A'}</h3>
                                  {getOfferStatusBadge(offer.status)}
                                  {request && (
                                    <div className="ml-auto">
                                      {getRequestStatusBadge(request.status)}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-baseline gap-6">
                                  {/* Prix déclaré par le client */}
                                  <div className="flex-1">
                                    <div className="text-xs text-muted-foreground mb-1">Prix déclaré par le client</div>
                                    <div className="text-2xl font-bold text-secondary">
                                      {clientPrice ? formatCurrency(clientPrice) : 'Non défini'}
                                    </div>
                                  </div>
                                  {/* Mon prix (offre) */}
                                  <div className="flex-1">
                                    <div className="text-xs text-muted-foreground mb-1">Mon prix</div>
                                    <div className="text-2xl font-bold text-secondary">
                                      {formatCurrency(offerPrice)}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm text-muted-foreground flex items-center gap-2 pt-2">
                                  <Calendar className="w-4 h-4" />
                                  <div>
                                    <span className="font-medium">Créée le </span>
                                    <span>{formatDate(offer.created_at)}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Informations du trajet */}
                              {request && (
                                <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Trajet
                                  </h4>
                                  <div className="space-y-1.5 text-sm">
                                    <div className="flex items-center gap-2 text-foreground">
                                      <span className="font-medium">De:</span>
                                      <span className="truncate">
                                        {request.from_location_detail?.city ||
                                         request.from_location_detail?.label ||
                                         request.from_location_detail?.name ||
                                         request.from_location?.city ||
                                         request.from_location?.label ||
                                         request.from_location?.name ||
                                         'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-foreground">
                                      <span className="font-medium">Vers:</span>
                                      <span className="truncate">
                                        {request.to_location_detail?.city ||
                                         request.to_location_detail?.label ||
                                         request.to_location_detail?.name ||
                                         request.to_location?.city ||
                                         request.to_location?.label ||
                                         request.to_location?.name ||
                                         'N/A'}
                                      </span>
                                    </div>
                                    {request.preferred_pickup_date && (
                                      <div className="flex items-center gap-2 text-muted-foreground mt-2 pt-2 border-t border-border">
                                        <Calendar className="w-4 h-4" />
                                        <span className="text-sm">Ramassage: <span className="font-semibold text-foreground">{formatDate(request.preferred_pickup_date)}</span></span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Informations du véhicule */}
                              {vehicle && (
                                <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                    <Truck className="w-4 h-4" />
                                    Véhicule
                                  </h4>
                                  <div className="space-y-2">
                                    {(vehicle.plate_number || vehicle.license_plate) && (
                                      <div className="flex items-center gap-2">
                                        <Truck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <div className="text-xs text-muted-foreground">Plaque</div>
                                          <div className="text-sm font-semibold text-foreground truncate">
                                            {vehicle.plate_number || vehicle.license_plate}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {(vehicle.vehicle_type_label || vehicle.vehicle_type?.name) && (
                                      <div className="flex items-center gap-2">
                                        <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <div className="text-xs text-muted-foreground">Type</div>
                                          <div className="text-sm font-semibold text-foreground truncate">
                                            {vehicle.vehicle_type_label || vehicle.vehicle_type?.name}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Message */}
                              {offer.message && (
                                <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                                  <div className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-semibold text-foreground mb-1">Message</div>
                                      <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed line-clamp-3">{offer.message}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            {offer.status === 'ACCEPTED' && request?.status === 'ASSIGNED' && (
                              <div className="flex flex-col gap-2 pt-4 border-t border-border">
                                <Button
                                  variant="default"
                                  onClick={() => {
                                    setActiveTab('missions');
                                  }}
                                  className="w-full text-sm py-3"
                                >
                                  <Truck className="w-4 h-4 mr-2" />
                                  Voir la mission
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Dialogue pour faire une offre */}
        <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Faire une offre</DialogTitle>
              <DialogDescription>
                Proposez votre prix et votre véhicule pour cette demande.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="price">Prix proposé (GNF) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={offerData.price}
                  onChange={(e) => setOfferData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="Ex: 500000"
                />
                {/* Calcul en temps réel de la commission */}
                {offerData.price && selectedRequest && parseFloat(offerData.price) > 0 && (() => {
                  const price = parseFloat(offerData.price);
                  let commission = 0;
                  let transporterAmount = price;

                  if (selectedRequest.commission_type === 'PERCENTAGE' && selectedRequest.commission_percentage) {
                    commission = price * (selectedRequest.commission_percentage / 100);
                    transporterAmount = price - commission;
                  } else if (selectedRequest.commission_type === 'FIXED_AMOUNT' && selectedRequest.commission_fixed_amount) {
                    commission = parseFloat(selectedRequest.commission_fixed_amount);
                    transporterAmount = price - commission;
                  }

                  return (
                    <div className="mt-3 p-4 bg-muted/50 rounded-lg border border-border space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Commission plateforme:</span>
                        <span className="text-sm font-semibold text-orange-500">
                          {commission > 0 ? formatCurrency(commission) : 'Aucune commission'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Montant net que vous recevrez:</span>
                        <span className="text-lg font-bold text-green-500">
                          {formatCurrency(transporterAmount)}
                        </span>
                      </div>
                      {selectedRequest.commission_type === 'PERCENTAGE' && selectedRequest.commission_percentage && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Commission de {selectedRequest.commission_percentage}% appliquée
                        </p>
                      )}
                      {selectedRequest.commission_type === 'FIXED_AMOUNT' && selectedRequest.commission_fixed_amount && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Commission fixe de {formatCurrency(selectedRequest.commission_fixed_amount)} appliquée
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div>
                <Label htmlFor="vehicle">Véhicule *</Label>
                <Select
                  value={offerData.vehicle}
                  onValueChange={(value) => setOfferData(prev => ({ ...prev, vehicle: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un véhicule" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.filter(v => v.status === 'AVAILABLE').map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.plate_number} - {vehicle.vehicle_type?.label || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">Message (optionnel)</Label>
                <Textarea
                  id="message"
                  value={offerData.message}
                  onChange={(e) => setOfferData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Ajoutez un message pour le client..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOfferDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmitOffer}>
                <Send className="w-4 h-4 mr-2" />
                Envoyer l'offre
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal pour les paiements */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Paiements - Mission #{selectedMission?.id?.slice(-8)}
              </DialogTitle>
              <DialogDescription>
                {selectedMission && (
                  <div className="mt-2">
                    <p className="text-base">Prix du transport: <span className="font-bold text-secondary text-lg">{formatCurrency(selectedMission.transporter_amount || 0)}</span></p>
                    <p className="text-sm">Total payé: <span className="font-semibold text-secondary">{formatCurrency(selectedMission.total_paid)}</span> ({selectedMission.payment_percentage || 0}%)</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>

            {selectedMission && (
              <div className="mt-4 space-y-4">
                {selectedMission.payments && selectedMission.payments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucun paiement enregistré pour cette mission
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedMission.payments.map((payment) => (
                      <Card key={payment.id} className="bg-card/50 border-border">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-semibold">Paiement #{payment.id?.slice(-8)}</h4>
                                {getPaymentStatusBadgeForPayment(payment.status)}
                                <Badge variant="outline" className="flex items-center gap-1">
                                  {getPaymentMethodIcon()}
                                  <span>{payment.method_code || 'N/A'}</span>
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <Label className="text-muted-foreground text-xs">Montant</Label>
                                  <p className="mt-1 font-semibold text-secondary">{formatCurrency(payment.amount)}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground text-xs">Date</Label>
                                  <p className="mt-1">{formatDate(payment.created_at)}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground text-xs">Référence</Label>
                                  <p className="mt-1 font-mono text-xs">{payment.provider_reference || 'N/A'}</p>
                                </div>
                                {payment.proof_url && (
                                  <div>
                                    <Label className="text-muted-foreground text-xs">Preuve</Label>
                                    <div className="flex gap-2 mt-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewProof(payment.proof_url)}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        Voir
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDownloadProof(payment.proof_url, payment.id)}
                                      >
                                        <Download className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal pour afficher la preuve de paiement */}
        <Dialog open={proofDialogOpen} onOpenChange={setProofDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Preuve de paiement</DialogTitle>
            </DialogHeader>
            {selectedProof && (
              <div className="mt-4">
                {selectedProof.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={selectedProof}
                    className="w-full h-[600px] border rounded"
                    title="Preuve de paiement PDF"
                  />
                ) : (
                  <img
                    src={selectedProof}
                    alt="Preuve de paiement"
                    className="w-full h-auto rounded border"
                  />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal pour afficher les détails complets de la demande */}
        <Dialog open={requestDetailsDialogOpen} onOpenChange={setRequestDetailsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Détails de la demande {requestDetails ? `#${requestDetails.id?.slice(-8)}` : ''}
              </DialogTitle>
              <DialogDescription>
                {requestDetails && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {getUrgencyBadge(requestDetails.urgency_level)}
                    {getPaymentTermsBadge(requestDetails.payment_terms)}
                    {requestDetails.status && (
                      <Badge className="bg-blue-500 text-white">
                        {constants ? constantsUtils.getLabel(constants.transport_request_status, requestDetails.status) : requestDetails.status}
                      </Badge>
                    )}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>

            {requestDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Chargement des détails...</div>
              </div>
            ) : requestDetails ? (
              <div className="space-y-6 mt-4">
                {/* Section principale - Informations critiques mises en évidence */}
                <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30">
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {/* Prix déclaré par le client - Mise en évidence spéciale */}
                      {requestDetails.declared_value ? (
                        <div className="text-center py-6 bg-yellow-500/10 rounded-lg border-2 border-yellow-500/30 shadow-lg">
                          <Label className="text-xs text-yellow-400/80 uppercase tracking-wider font-semibold mb-3 block">Prix déclaré par le client</Label>
                          <div className="mt-2">
                            <span className="text-5xl font-bold text-yellow-400 drop-shadow-lg">
                              {formatCurrency(requestDetails.declared_value)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-500/10 rounded-lg border-2 border-gray-500/30">
                          <Label className="text-xs text-gray-400/80 uppercase tracking-wider font-semibold mb-3 block">Prix</Label>
                          <div className="mt-2">
                            <span className="text-lg font-semibold text-gray-400">
                              Le client n'a pas défini de prix
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Commission de la plateforme - Affichage clair */}
                      {requestDetails.commission_type && (
                        <div className="mt-4 p-4 bg-purple-500/10 rounded-lg border-2 border-purple-500/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Percent className="w-5 h-5 text-purple-400" />
                              <Label className="text-sm font-semibold text-purple-400">Commission de la plateforme</Label>
                            </div>
                            <div className="text-right">
                              {requestDetails.commission_type === 'PERCENTAGE' && requestDetails.commission_percentage ? (
                                <div>
                                  <span className="text-3xl font-bold text-purple-400">
                                    {requestDetails.commission_percentage}%
                                  </span>
                                  <p className="text-xs text-purple-300/80 mt-1">
                                    Appliquée sur le prix proposé
                                  </p>
                                </div>
                              ) : requestDetails.commission_type === 'FIXED_AMOUNT' && requestDetails.commission_fixed_amount ? (
                                <div>
                                  <span className="text-2xl font-bold text-purple-400">
                                    {formatCurrency(requestDetails.commission_fixed_amount)}
                                  </span>
                                  <p className="text-xs text-purple-300/80 mt-1">
                                    Montant fixe déduit
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-purple-500/30">
                            <p className="text-xs text-purple-300/70">
                              {requestDetails.commission_type === 'PERCENTAGE'
                                ? `Cette commission sera calculée automatiquement sur le prix que vous proposerez. Exemple : si vous proposez 1 000 000 GNF, la commission sera de ${requestDetails.commission_percentage}% = ${formatCurrency((1000000 * requestDetails.commission_percentage / 100))} GNF, et vous recevrez ${formatCurrency(1000000 - (1000000 * requestDetails.commission_percentage / 100))} GNF.`
                                : `Cette commission fixe sera déduite du prix que vous proposerez. Exemple : si vous proposez 1 000 000 GNF, la commission sera de ${formatCurrency(requestDetails.commission_fixed_amount)} GNF, et vous recevrez ${formatCurrency(1000000 - requestDetails.commission_fixed_amount)} GNF.`
                              }
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Trajet - Mise en évidence */}
                      <div className="bg-card/50 rounded-lg p-4 border border-border">
                        <Label className="text-sm font-semibold text-foreground mb-3 block">Trajet</Label>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-primary" />
                              <div>
                                <p className="font-semibold text-lg">
                                  {requestDetails.from_location_detail?.city || requestDetails.from_location_detail?.label ||
                                   requestDetails.from_location?.city || requestDetails.from_location?.label || 'N/A'}
                                </p>
                                {requestDetails.from_location_detail?.address && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {requestDetails.from_location_detail.address}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <Navigation className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <div>
                                <p className="font-semibold text-lg">
                                  {requestDetails.to_location_detail?.city || requestDetails.to_location_detail?.label ||
                                   requestDetails.to_location?.city || requestDetails.to_location?.label || 'N/A'}
                                </p>
                                {requestDetails.to_location_detail?.address && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {requestDetails.to_location_detail.address}
                                  </p>
                                )}
                              </div>
                              <MapPin className="w-5 h-5 text-primary" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Dates - Format long */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {requestDetails.preferred_pickup_date && (
                          <div className="bg-card/50 rounded-lg p-4 border border-border">
                            <Label className="text-sm font-semibold text-foreground mb-2 block">Date d'enlèvement</Label>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-primary" />
                              <span className="font-semibold text-base">
                                {formatDateLong(requestDetails.preferred_pickup_date)}
                              </span>
                            </div>
                          </div>
                        )}
                        {requestDetails.preferred_delivery_date && (
                          <div className="bg-card/50 rounded-lg p-4 border border-border">
                            <Label className="text-sm font-semibold text-foreground mb-2 block">Date de livraison</Label>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-primary" />
                              <span className="font-semibold text-base">
                                {formatDateLong(requestDetails.preferred_delivery_date)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Informations de transport */}
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Informations de transport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {requestDetails.preferred_vehicle_type_detail && (
                        <div>
                          <Label className="text-muted-foreground">Type de véhicule souhaité</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Truck className="w-4 h-4" />
                            <span className="font-medium">
                              {requestDetails.preferred_vehicle_type_detail.label ||
                               requestDetails.preferred_vehicle_type_detail.code || 'N/A'}
                            </span>
                          </div>
                        </div>
                      )}
                      {requestDetails.handling_responsibility && (
                        <div>
                          <Label className="text-muted-foreground">Manutention</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Package className="w-4 h-4" />
                            <span className="font-medium">
                              {constants ? constantsUtils.getLabel(constants.handling_responsibility, requestDetails.handling_responsibility) : requestDetails.handling_responsibility}
                            </span>
                          </div>
                        </div>
                      )}
                      {requestDetails.goods_type && (
                        <div>
                          <Label className="text-muted-foreground">Type de marchandise</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Package className="w-4 h-4" />
                            <span className="font-medium">
                              {constants ? constantsUtils.getLabel(constants.transport_domains, requestDetails.goods_type) : requestDetails.goods_type}
                            </span>
                          </div>
                        </div>
                      )}
                      {requestDetails.estimated_weight_kg && (
                        <div>
                          <Label className="text-muted-foreground">Poids estimatif</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Package className="w-4 h-4" />
                            <span className="font-medium">{parseFloat(requestDetails.estimated_weight_kg).toLocaleString('fr-FR')} kg</span>
                          </div>
                        </div>
                      )}
                      {requestDetails.estimated_volume_m3 && (
                        <div>
                          <Label className="text-muted-foreground">Volume estimatif</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Package className="w-4 h-4" />
                            <span className="font-medium">{parseFloat(requestDetails.estimated_volume_m3).toLocaleString('fr-FR')} m³</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Articles/Cargo */}
                {requestDetails.cargo_items && requestDetails.cargo_items.length > 0 && (
                  <Card className="bg-card/50 border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Articles ({requestDetails.cargo_items.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {requestDetails.cargo_items.map((item, idx) => (
                          <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium">{item.description || `Article ${idx + 1}`}</p>
                                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                  {item.quantity && (
                                    <span className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      Quantité: {item.quantity}
                                    </span>
                                  )}
                                  {item.weight_kg && (
                                    <span className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      Poids: {item.weight_kg} kg
                                    </span>
                                  )}
                                  {item.volume_m3 && (
                                    <span className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      Volume: {item.volume_m3} m³
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Description */}
                {requestDetails.description && (
                  <Card className="bg-card/50 border-border">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Description
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">{requestDetails.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Aucun détail disponible</div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRequestDetailsDialogOpen(false)}>
                Fermer
              </Button>
              {requestDetails && (
                <Button
                  onClick={() => {
                    setRequestDetailsDialogOpen(false);
                    handleMakeOffer(requestDetails);
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Faire une offre
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default TransporterMissionsRequests;
